import { useCallback } from 'react';
import { fetchResourceFiles, fetchResourceFileStats, reingestFile } from 'js/actions';
import { FileUtil } from 'new/utils/FileUtil';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { FileItemListRow } from 'new/elements/FileItemsList/types';

interface UseReingestFilesProps {
  resourceId: string;
  resource: any;
  sort: string;
  status: string;
}

/**
 *
 * This hook provides functionality to reingest files based on the given resource parameters.
 * It dispatches the reingestFile action with proper formatting and pagination settings,
 * and offers a callback mechanism for success handling.

 * @example
 *   const { handleReingest } = useReingestFiles({ resourceId, resource, sort, status });
 *
 *   const onReingestionClick = () => {
 *     handleReingest(selectedFiles, {
 *       onSuccess: () => { }
 *     });
 *   };
 */
export const useReingestFiles = ({ resourceId, resource, sort, status }: UseReingestFilesProps) => {
  const dispatch = useAppDispatch();

  const filters = {
    page: 1,
    size: 50,
    orderby: sort,
    status: status === 'all' ? '' : status,
  };

  const handleReingest = useCallback(
    (files: Partial<FileItemListRow>[], { onSuccess }: { onSuccess?: () => void }) => {
      if (files?.length === 0) return;
      if (!Array.isArray(files)) return;

      const mappedFiles = FileUtil.mapFilesForReingestion(files, resource);

      if (mappedFiles.length === 0) return;

      dispatch(reingestFile(resourceId, mappedFiles, filters, false))
        .then(() => onSuccess?.())
        .catch(console.error);
    },
    [resourceId, resource, sort, status],
  );

  const loadFileStats = async (id: any, resourceType: string) => {
    dispatch(fetchResourceFileStats(id, resourceType));
    dispatch(fetchResourceFiles(id, resourceType, filters, sort));
  };

  return { handleReingest, loadFileStats };
};
